import React, { useEffect, useMemo, useState } from 'react';
import {
    Drawer,
    Spin,
    Tag,
    Card,
    Collapse,
    Table,
    Button,
    Space,
    Descriptions,
    Typography,
    Empty,
    Statistic,
    Progress,
    Alert,
    Segmented,
    Tooltip,
    Tabs,
} from 'antd';
import {
    DownloadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    WarningOutlined,
    ClockCircleOutlined,
    UserOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { EvalRunResultsResponse, EvalRunStatus, EvalPerQuestionResult, ConsistencyMetrics, TaskResultSqlOutput } from './evalTypes';
import SkillStudioVizPreview from './SkillStudioVizPreview';

const { Text, Title } = Typography;

const statusConfig: Record<EvalRunStatus, { color: string; label: string }> = {
    pending: { color: 'default', label: 'Pending' },
    running: { color: 'processing', label: 'Running' },
    completed: { color: 'success', label: 'Completed' },
    failed: { color: 'error', label: 'Failed' },
};

function formatDuration(startedAt: string, completedAt: string | null): string {
    if (!completedAt) return '-';
    const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
    const sec = Math.round(ms / 1000);
    if (sec < 60) return `${sec}s`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

function titleCase(s: string): string {
    return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function humanizeMetricKey(key: string): string {
    const segment = key.includes('.') ? key.split('.').pop()! : key;
    return titleCase(segment);
}

/** Normalized score (0-1) to color */
function scoreColor(pct: number): string {
    if (pct >= 70) return '#52c41a';
    if (pct >= 40) return '#faad14';
    return '#ff4d4f';
}

/** Detect metric scale and return percent for Progress bar, or null to skip */
function metricPercent(value: number): number | null {
    if (value >= 0 && value <= 1) return Math.round(value * 100);
    if (value >= 0 && value <= 5) return Math.round((value / 5) * 100);
    return null;
}

/** Filter out noisy metric keys: gpt_* duplicates, token counts, binary_aggregate */
function isDisplayMetric(key: string): boolean {
    const last = key.includes('.') ? key.split('.').pop()! : key;
    if (last.startsWith('gpt_')) return false;
    if (last.endsWith('_tokens') || last.endsWith('_prompt_tokens') || last.endsWith('_completion_tokens') || last.endsWith('_total_tokens')) return false;
    if (last === 'binary_aggregate') return false;
    return true;
}

function groupMetricsByAgent(
    metrics: Record<string, unknown>,
): Record<string, Record<string, number>> {
    const grouped: Record<string, Record<string, number>> = {};
    for (const [key, value] of Object.entries(metrics)) {
        if (typeof value !== 'number') continue;
        const prefix = key.includes('.')
            ? key.substring(0, key.indexOf('.'))
            : key.split('_')[0];
        const agentName = prefix.split('_')[0];
        if (!grouped[agentName]) grouped[agentName] = {};
        grouped[agentName][key] = value;
    }
    return grouped;
}

/** Extract unique agent names from gate_metrics across all questions */
function extractAgentGateNames(rows: EvalPerQuestionResult[]): string[] {
    const agents = new Set<string>();
    for (const row of rows) {
        if (row.gate_metrics) {
            for (const key of Object.keys(row.gate_metrics)) {
                agents.add(key.split('.')[0]);
            }
        }
    }
    return Array.from(agents).sort();
}

/** Summarize per-agent gate pass/fail for a single question */
function agentGateSummary(gateMetrics: Record<string, boolean> | undefined, agent: string): 'pass' | 'fail' | 'mixed' | null {
    if (!gateMetrics) return null;
    const entries = Object.entries(gateMetrics).filter(([k]) => k.startsWith(`${agent}.`));
    if (!entries.length) return null;
    const allPass = entries.every(([, v]) => v);
    const allFail = entries.every(([, v]) => !v);
    if (allPass) return 'pass';
    if (allFail) return 'fail';
    return 'mixed';
}

type PerQuestionRow = EvalPerQuestionResult;

type QuestionFilter = 'all' | 'passed' | 'failed' | 'errors';

export interface SkillStudioEvalRunDetailDrawerProps {
    open: boolean;
    onClose: () => void;
    loading: boolean;
    results: EvalRunResultsResponse | null;
    downloading: boolean;
    onDownload: (format: 'csv' | 'json') => void;
}

const SkillStudioEvalRunDetailDrawer: React.FC<SkillStudioEvalRunDetailDrawerProps> = ({
    open,
    onClose,
    loading,
    results,
    downloading,
    onDownload,
}) => {
    const [questionFilter, setQuestionFilter] = useState<QuestionFilter>('all');

    useEffect(() => {
        if (!open) setQuestionFilter('all');
    }, [open]);

    const run = results?.run;

    const questionRows: PerQuestionRow[] = useMemo(
        () => results?.per_question_results ?? [],
        [results],
    );

    // --- Pass rate stats ---
    const passCount = questionRows.filter((r) => r.row_pass === true).length;
    const failCount = questionRows.filter((r) => r.row_pass === false).length;
    const errorCount = questionRows.filter((r) => r.error != null).length;
    const totalCount = questionRows.length;

    // --- Filter ---
    const filteredRows = useMemo(() => {
        switch (questionFilter) {
            case 'passed':
                return questionRows.filter((r) => r.row_pass === true);
            case 'failed':
                return questionRows.filter((r) => r.row_pass === false);
            case 'errors':
                return questionRows.filter((r) => r.error != null);
            default:
                return questionRows;
        }
    }, [questionRows, questionFilter]);

    // --- Dynamic per-agent gate columns from gate_metrics ---
    const agentGateNames = useMemo(() => extractAgentGateNames(questionRows), [questionRows]);

    const questionColumns: ColumnsType<PerQuestionRow> = useMemo(() => {
        const base: ColumnsType<PerQuestionRow> = [
            { title: '#', dataIndex: 'question_index', key: 'idx', width: 50, align: 'center' },
            { title: 'Question', dataIndex: 'query', key: 'query', ellipsis: true },
            {
                title: 'Result',
                dataIndex: 'row_pass',
                key: 'row_pass',
                width: 80,
                align: 'center',
                render: (v: boolean | null, record: PerQuestionRow) =>
                    record.error ? (
                        <Tooltip title={record.error}>
                            <WarningOutlined style={{ color: '#faad14', fontSize: 16 }} />
                        </Tooltip>
                    ) : v == null ? (
                        '-'
                    ) : v ? (
                        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                    ) : (
                        <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
                    ),
            },
        ];

        const agentCols: ColumnsType<PerQuestionRow> = agentGateNames.map((agent) => ({
            title: titleCase(agent),
            key: `gate_${agent}`,
            width: 90,
            align: 'center' as const,
            render: (_: unknown, record: PerQuestionRow) => {
                const summary = agentGateSummary(record.gate_metrics, agent);
                if (summary === 'pass') return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
                if (summary === 'fail') return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
                if (summary === 'mixed') return (
                    <Tooltip title="Some metrics passed, some failed">
                        <WarningOutlined style={{ color: '#faad14' }} />
                    </Tooltip>
                );
                return '-';
            },
        }));

        return [...base, ...agentCols];
    }, [agentGateNames]);

    // --- Metrics ---
    const metricsGrouped = results?.overview_metrics
        ? groupMetricsByAgent(results.overview_metrics)
        : null;

    const consistency = useMemo<ConsistencyMetrics | null>(() => {
        const raw = results?.overview_metrics?.consistency;
        if (!raw || typeof raw !== 'object') return null;
        return raw as ConsistencyMetrics;
    }, [results]);

    // --- Expanded row ---
    const renderExpandedRow = (record: PerQuestionRow) => {
        const gt = record.ground_truth as Record<string, unknown> | null;
        const ae = record.agent_evaluations;
        const ao = record.agent_outputs;
        const gm = record.gate_metrics;

        const gateEntries = gm ? Object.entries(gm) : [];
        const agentEvalEntries = ae ? Object.entries(ae) : [];

        // ── Formatting helpers ──
        const formatOutput = (value: unknown): React.ReactNode => {
            if (value == null) return <Text type="secondary">-</Text>;
            if (typeof value === 'string') {
                if (value.length > 300) return <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 200, overflow: 'auto' }}>{value}</div>;
                return <span style={{ wordBreak: 'break-word' }}>{value}</span>;
            }
            if (typeof value === 'boolean') return value ? 'true' : 'false';
            if (typeof value === 'number') return String(value);
            return (
                <pre style={{ fontSize: 11, margin: 0, maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#fafafa', padding: 8, borderRadius: 4 }}>
                    {JSON.stringify(value, null, 2)}
                </pre>
            );
        };

        const tryParseJson = (v: unknown): unknown => {
            if (typeof v === 'string') {
                try { return JSON.parse(v); } catch { /* noop */ }
                // Handle NDJSON (newline-delimited JSON) and "[label]: json" prefixes per line
                if (v.includes('\n')) {
                    const items = v.split('\n').filter(Boolean).map((line) => {
                        try { return JSON.parse(line); } catch { /* noop */ }
                        const stripped = line.replace(/^\[[^\]]*\]:\s*/, '');
                        if (stripped !== line) { try { return JSON.parse(stripped); } catch { /* noop */ } }
                        return null;
                    }).filter(Boolean);
                    if (items.length > 0) return items;
                }
                const stripped = v.replace(/^\[[^\]]*\]:\s*/, '');
                if (stripped !== v) { try { return JSON.parse(stripped); } catch { /* noop */ } }
                return v;
            }
            return v;
        };

        const toTableArray = (raw: unknown): unknown[] => {
            if (raw == null) return [];
            const parsed = tryParseJson(raw);
            if (Array.isArray(parsed)) {
                return parsed.flatMap(item => Array.isArray(item) ? item : [item]);
            }
            if (parsed && typeof parsed === 'object') {
                const obj = parsed as Record<string, unknown>;
                if ('columns' in obj && 'rows' in obj) return [obj];
                return Object.values(obj).filter(v => v && typeof v === 'object');
            }
            return [];
        };

        const renderSqlTable = (table: unknown, idx: number): React.ReactNode => {
            const t = (typeof table === 'object' && table) ? table as Record<string, unknown> : null;
            if (!t) return formatOutput(table);
            const cols = t.columns as string[] | undefined;
            const rawRows = t.rows as unknown[] | undefined;
            if (!cols?.length || !rawRows?.length) return formatOutput(table);
            const rows = rawRows.map((row) => {
                if (Array.isArray(row)) {
                    const obj: Record<string, unknown> = {};
                    cols.forEach((col, ci) => { obj[col] = ci < row.length ? row[ci] : null; });
                    return obj;
                }
                return row as Record<string, unknown>;
            });
            return (
                <Table
                    key={idx}
                    size="small"
                    pagination={{ pageSize: 10, size: 'small', hideOnSinglePage: true }}
                    scroll={{ x: true }}
                    dataSource={rows.map((r, i) => ({ ...r, _key: i }))}
                    rowKey="_key"
                    columns={cols.map((c) => ({
                        title: c, dataIndex: c, key: c, ellipsis: true,
                        render: (v: unknown) => v == null ? '-' : String(v),
                    }))}
                />
            );
        };

        const renderVizOutput = (viz: unknown, idx: number, fallbackSql?: TaskResultSqlOutput): React.ReactNode => {
            const v = (typeof viz === 'object' && viz) ? viz as Record<string, unknown> : null;
            if (!v) return formatOutput(viz);
            const data = (v.viz_data ?? v) as Record<string, unknown>;
            const chartType = (data.chart_type ?? v.chart_type) as string | undefined;
            const chartTitle = (data.chartTitle ?? data.chart_title ?? v.chartTitle ?? v.chart_title) as string | undefined;
            return (
                <div key={idx}>
                    <SkillStudioVizPreview
                        chartType={chartType}
                        chartTitle={chartTitle}
                        vizData={data}
                        sqlOutput={fallbackSql}
                    />
                </div>
            );
        };

        const renderNarrativeContent = (value: unknown): React.ReactNode => {
            if (typeof value === 'string') {
                return <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 300, overflow: 'auto' }}>{value}</div>;
            }
            if (value && typeof value === 'object') {
                const narr = value as Record<string, unknown>;
                const overview = narr.overview as string | undefined;
                const takeaways = narr.key_takeaways as string[] | undefined;
                const assumptions = narr.assumptions as string[] | undefined;
                const formulae = narr.formulae as string[] | undefined;
                const renderList = (title: string, items?: string[]) => (
                    items?.length ? (
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>{title}</Text>
                            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12 }}>
                                {items.map((item, i) => <li key={i} style={{ marginBottom: 4 }}>{item}</li>)}
                            </ul>
                        </div>
                    ) : null
                );
                if (overview || takeaways?.length || assumptions?.length || formulae?.length) {
                    return (
                        <div>
                            {overview && <div style={{ fontSize: 12, marginBottom: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{overview}</div>}
                            {renderList('Key Takeaways', takeaways)}
                            {renderList('Assumptions', assumptions)}
                            {renderList('Formulae', formulae)}
                        </div>
                    );
                }
            }
            return formatOutput(value);
        };

        const renderTaskEvalMetrics = (task: Record<string, unknown>): React.ReactNode => {
            const entries = Object.entries(task).filter(([k]) => k !== 'task');
            if (entries.length === 0) return <Text type="secondary">No evaluation metrics</Text>;
            return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                    {entries.map(([key, value]) => {
                        const label = titleCase(key);
                        if (typeof value === 'boolean') {
                            return (
                                <div key={key} style={{ padding: '6px 10px', background: '#fafafa', borderRadius: 4 }}>
                                    <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{label}</Text>
                                    <Tag color={value ? 'success' : 'error'}>{value ? 'Yes' : 'No'}</Tag>
                                </div>
                            );
                        }
                        if (typeof value === 'number') {
                            const isRate = value >= 0 && value <= 1 && (key.includes('rate') || key.includes('score') || key.includes('overlap'));
                            return (
                                <div key={key} style={{ padding: '6px 10px', background: '#fafafa', borderRadius: 4 }}>
                                    <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{label}</Text>
                                    {isRate
                                        ? <Progress percent={Math.round(value * 100)} size="small" strokeColor={scoreColor(Math.round(value * 100))} />
                                        : <Text strong>{String(value)}</Text>}
                                </div>
                            );
                        }
                        if (typeof value === 'string' && value.length > 200) {
                            return (
                                <div key={key} style={{ padding: '6px 10px', background: '#fafafa', borderRadius: 4, gridColumn: '1 / -1' }}>
                                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>{label}</Text>
                                    <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 150, overflow: 'auto' }}>{value}</div>
                                </div>
                            );
                        }
                        return (
                            <div key={key} style={{ padding: '6px 10px', background: '#fafafa', borderRadius: 4 }}>
                                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{label}</Text>
                                <Text strong style={{ fontSize: 12 }}>{value == null ? '-' : String(value)}</Text>
                            </div>
                        );
                    })}
                </div>
            );
        };

        const renderGtPerTask = (items: unknown[]): React.ReactNode => (
            <Collapse
                size="small"
                items={items.map((item, idx) => {
                    const t = item as Record<string, unknown>;
                    const taskName = (t.task ?? `Task ${idx + 1}`) as string;
                    return {
                        key: String(idx),
                        label: <Text strong>{titleCase(taskName)}</Text>,
                        children: (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {Array.isArray(t.sql_queries) && t.sql_queries.length > 0 && (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>SQL Queries</Text>
                                        {(t.sql_queries as string[]).map((q, qi) => (
                                            <pre key={qi} style={{ fontSize: 11, margin: '0 0 4px', padding: 8, background: '#f6f8fa', borderRadius: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{q}</pre>
                                        ))}
                                    </div>
                                )}
                                {Array.isArray(t.sql_db_output) && t.sql_db_output.length > 0 && (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>SQL Output</Text>
                                        {(t.sql_db_output as unknown[]).map((tbl, ti) => renderSqlTable(tbl, ti))}
                                    </div>
                                )}
                                {t.narrative != null ? (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Narrative</Text>
                                        {renderNarrativeContent(t.narrative)}
                                    </div>
                                ) : null}
                                {t.viz != null ? (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Visualization</Text>
                                        {(() => {
                                            const vizItems = Array.isArray(t.viz) ? t.viz as unknown[] : [t.viz];
                                            const sqlTables = Array.isArray(t.sql_db_output) ? t.sql_db_output as TaskResultSqlOutput[] : [];
                                            return vizItems.map((v, vi) => {
                                                const vObj = (typeof v === 'object' && v) ? v as Record<string, unknown> : {};
                                                const vData = (vObj.viz_data ?? vObj) as Record<string, unknown>;
                                                return (
                                                    <SkillStudioVizPreview
                                                        key={vi}
                                                        chartType={(vData.chart_type ?? vObj.chart_type) as string | undefined}
                                                        chartTitle={(vData.chartTitle ?? vData.chart_title ?? vObj.chartTitle ?? vObj.chart_title) as string | undefined}
                                                        vizData={vData}
                                                        sqlOutput={sqlTables[vi] ?? sqlTables[0]}
                                                    />
                                                );
                                            });
                                        })()}
                                    </div>
                                ) : null}
                            </div>
                        ),
                    };
                })}
            />
        );

        // ── Ground Truth structured render ──
        const knownGtFields: Array<{ key: string; label: string }> = [
            { key: 'GroundTruthIntent', label: 'Intent' },
            { key: 'GroundTruthRefine', label: 'Refine' },
            { key: 'GroundTruthTasks', label: 'Tasks' },
            { key: 'GroundTruthSummary', label: 'Summary' },
            { key: 'GroundTruthPlan', label: 'Plan' },
            { key: 'GroundTruthViz', label: 'Visualization' },
            { key: 'GroundTruthPerTask', label: 'Per Task' },
        ];
        const knownKeys = new Set(knownGtFields.map((f) => f.key));
        const unknownGtKeys = gt ? Object.keys(gt).filter((k) => !knownKeys.has(k)) : [];

        const renderGtValue = (key: string, value: unknown): React.ReactNode => {
            if (value == null) return <Text type="secondary">-</Text>;
            if (key === 'GroundTruthTasks' && Array.isArray(value)) {
                return <Space size={4} wrap>{value.map((t, i) => <Tag key={i}>{String(t)}</Tag>)}</Space>;
            }
            if (key === 'GroundTruthPlan' && Array.isArray(value)) {
                return (
                    <Table size="small" pagination={false} dataSource={value.map((p, i) => ({ ...p, _idx: i }))} rowKey="_idx"
                        columns={[{ title: 'Task', dataIndex: 'task', key: 'task' }, { title: 'Sub-query', dataIndex: 'sub_query', key: 'sub_query' }]}
                    />
                );
            }
            if (key === 'GroundTruthPerTask' && Array.isArray(value)) {
                return renderGtPerTask(value);
            }
            if (typeof value === 'object') return formatOutput(value);
            return <span>{String(value)}</span>;
        };

        // ── Agent Outputs: split per-task vs scalar ──
        const PER_TASK_OUTPUT_KEYS = new Set(['sql_output', 'viz_output', 'context', 'narrative', 'per_task', 'sql_query', 'sql_queries']);
        const scalarOutputs = ao ? Object.entries(ao).filter(([k]) => !PER_TASK_OUTPUT_KEYS.has(k)) : [];

        const sqlArr = ao ? toTableArray(ao.sql_output) : [];
        const vizArr = ao ? (() => {
            const raw = tryParseJson(ao.viz_output);
            if (Array.isArray(raw)) return raw;
            if (raw && typeof raw === 'object') return [raw];
            return [];
        })() : [];
        const ctxArr = ao ? toTableArray(ao.context) : [];
        const narrativeArr: unknown[] = ao?.narrative != null
            ? (Array.isArray(ao.narrative) ? ao.narrative : [ao.narrative])
            : [];
        const sqlQueryRaw = ao ? tryParseJson(ao.sql_query ?? ao.sql_queries) : undefined;
        const sqlQueries: string[] = (() => {
            if (!sqlQueryRaw) return [];
            if (typeof sqlQueryRaw === 'string') return [sqlQueryRaw];
            if (Array.isArray(sqlQueryRaw)) return sqlQueryRaw.filter((q): q is string => typeof q === 'string');
            return [];
        })();
        const perTaskRaw = ao?.per_task as Record<string, unknown> | undefined;
        const perTaskDetails: Array<Record<string, unknown>> | undefined = perTaskRaw ? (() => {
            let ptd = perTaskRaw.per_task_details;
            if (typeof ptd === 'string') { try { ptd = JSON.parse(ptd); } catch { return undefined; } }
            return Array.isArray(ptd) ? ptd as Array<Record<string, unknown>> : undefined;
        })() : undefined;
        const taskNames = perTaskDetails?.map(d => String(d.task)) ?? [];
        const numTasks = taskNames.length > 0
            ? taskNames.length
            : (sqlArr.length > 0 || vizArr.length > 0 || ctxArr.length > 0 || narrativeArr.length > 0 ? 1 : 0);
        const hasPerTaskContent = numTasks > 0 || perTaskRaw != null;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {record.error && (
                    <Alert type="warning" showIcon message={record.error} />
                )}

                {/* Gate Metrics */}
                {gateEntries.length > 0 && (
                    <Card size="small" title="Gate Metrics (per agent)">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 6 }}>
                            {gateEntries.map(([k, passed]) => (
                                <div key={k} style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '3px 8px', borderRadius: 4, fontSize: 12,
                                    background: passed ? '#f6ffed' : '#fff2f0',
                                    border: `1px solid ${passed ? '#b7eb8f' : '#ffccc7'}`,
                                }}>
                                    {passed
                                        ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                        : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                                    <Text style={{ fontSize: 12 }}>{k}</Text>
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                {/* Agent Evaluations */}
                {agentEvalEntries.length > 0 && (
                    <Card size="small" title="Agent Evaluations (scores, results & reasons)">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {agentEvalEntries.map(([agent, evaluators]) => (
                                <div key={agent}>
                                    <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>{titleCase(agent)}</Text>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                        {Object.entries(evaluators).map(([evalName, details]) => {
                                            const d = details as Record<string, unknown>;
                                            const result = d[`${evalName}_result`] as string | undefined;
                                            const reason = (d[`${evalName}_reason`] ?? d[`${evalName}_details`]) as string | undefined;
                                            const threshold = d[`${evalName}_threshold`] as number | undefined;
                                            const score = d[evalName] as number | undefined;
                                            const passed = result === 'pass';
                                            const failed = result === 'fail';
                                            const ptEvDetails: Array<Record<string, unknown>> | null = (() => {
                                                let raw = d.per_task_details;
                                                if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { return null; } }
                                                return Array.isArray(raw) ? raw as Array<Record<string, unknown>> : null;
                                            })();

                                            return (
                                                <div key={evalName} style={{
                                                    flex: '1 1 280px', minWidth: 260,
                                                    padding: '8px 12px', borderRadius: 6,
                                                    background: passed ? '#f6ffed' : failed ? '#fff2f0' : '#fafafa',
                                                    border: `1px solid ${passed ? '#b7eb8f' : failed ? '#ffccc7' : '#f0f0f0'}`,
                                                }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                                        <Space size={6}>
                                                            {passed && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
                                                            {failed && <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                                                            <Text strong style={{ fontSize: 12 }}>{titleCase(evalName)}</Text>
                                                        </Space>
                                                        <Space size={8}>
                                                            {score != null && (
                                                                <Text style={{ fontSize: 12 }}>
                                                                    <Text strong>{typeof score === 'number' ? score.toFixed(1) : score}</Text>
                                                                    {threshold != null && <Text type="secondary"> / {threshold}</Text>}
                                                                </Text>
                                                            )}
                                                            {result && (
                                                                <Tag color={passed ? 'success' : 'error'} style={{ margin: 0, fontSize: 11 }}>
                                                                    {result}
                                                                </Tag>
                                                            )}
                                                        </Space>
                                                    </div>
                                                    {reason && (
                                                        <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: '16px' }}>
                                                            {reason}
                                                        </Text>
                                                    )}
                                                    {ptEvDetails && ptEvDetails.length > 0 && (
                                                        <Collapse size="small" style={{ marginTop: 8 }} items={ptEvDetails.map((ptd, ptIdx) => ({
                                                            key: String(ptIdx),
                                                            label: (
                                                                <Space size={4}>
                                                                    <Text style={{ fontSize: 11 }}>{titleCase(String(ptd.task ?? `Task ${ptIdx + 1}`))}</Text>
                                                                    {ptd.matched != null && (
                                                                        ptd.matched
                                                                            ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 11 }} />
                                                                            : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 11 }} />
                                                                    )}
                                                                </Space>
                                                            ),
                                                            children: renderTaskEvalMetrics(ptd),
                                                        }))} />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                {/* Agent Responses - Scalar Fields */}
                {scalarOutputs.length > 0 && (
                    <Card size="small" title="Agent Responses">
                        <Descriptions size="small" column={1} bordered
                            labelStyle={{ width: 160, wordBreak: 'break-word' }}
                            contentStyle={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {scalarOutputs.map(([key, value]) => (
                                <Descriptions.Item key={key} label={titleCase(key)}>
                                    {formatOutput(value)}
                                </Descriptions.Item>
                            ))}
                        </Descriptions>
                    </Card>
                )}

                {/* Per-Task Outputs & Evaluation */}
                {ao && hasPerTaskContent && (
                    <Card size="small" title="Per-Task Outputs & Evaluation">
                        {perTaskRaw && (
                            <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                                {perTaskRaw.per_task_gt_count != null && (
                                    <Statistic title="GT Tasks" value={perTaskRaw.per_task_gt_count as number} valueStyle={{ fontSize: 16 }} />
                                )}
                                {perTaskRaw.per_task_matched_count != null && (
                                    <Statistic title="Matched" value={perTaskRaw.per_task_matched_count as number} valueStyle={{ fontSize: 16 }} />
                                )}
                                {perTaskRaw.per_task_match_rate != null && (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Match Rate</Text>
                                        <div><Progress percent={Math.round((perTaskRaw.per_task_match_rate as number) * 100)} size="small" style={{ width: 120 }} /></div>
                                    </div>
                                )}
                            </div>
                        )}
                        <Collapse
                            size="small"
                            defaultActiveKey={numTasks <= 1 ? ['0'] : undefined}
                            items={Array.from({ length: numTasks || 1 }, (_, i) => {
                                const taskName = taskNames[i] ?? (numTasks <= 1 ? 'Task' : `Task ${i + 1}`);
                                const taskEval = perTaskDetails?.[i];
                                const isSingle = numTasks <= 1;
                                const taskSqlItems = isSingle ? sqlArr : (sqlArr[i] != null ? [sqlArr[i]] : []);
                                const taskVizItems = isSingle ? vizArr : (vizArr[i] != null ? [vizArr[i]] : []);
                                const taskCtxItems = isSingle ? ctxArr : (ctxArr[i] != null ? [ctxArr[i]] : []);
                                const taskNarrItems = isSingle ? narrativeArr : (narrativeArr[i] != null ? [narrativeArr[i]] : []);
                                const taskSqlQueries = isSingle ? sqlQueries : (sqlQueries[i] != null ? [sqlQueries[i]] : []);
                                const firstSqlTable = taskSqlItems[0] as Record<string, unknown> | undefined;
                                const sqlFallback: TaskResultSqlOutput | undefined = firstSqlTable && Array.isArray(firstSqlTable.columns) && Array.isArray(firstSqlTable.rows)
                                    ? { columns: firstSqlTable.columns as string[], rows: firstSqlTable.rows as Record<string, unknown>[] }
                                    : undefined;
                                const tabItems = [
                                    taskSqlQueries.length > 0 ? { key: 'query', label: 'SQL Query', children: (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {taskSqlQueries.map((q, qi) => (
                                                <pre key={qi} style={{ fontSize: 11, margin: 0, padding: 8, background: '#f6f8fa', borderRadius: 4, whiteSpace: 'pre-wrap' as const, wordBreak: 'break-word' as const, overflow: 'auto', maxHeight: 300 }}>{q}</pre>
                                            ))}
                                        </div>
                                    ) } : null,
                                    taskSqlItems.length > 0 ? { key: 'sql', label: 'SQL Output', children: (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                            {taskSqlItems.map((tbl, ti) => renderSqlTable(tbl, ti))}
                                        </div>
                                    ) } : null,
                                    taskCtxItems.length > 0 ? { key: 'context', label: 'Context Data', children: (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                            {taskCtxItems.map((tbl, ti) => renderSqlTable(tbl, ti))}
                                        </div>
                                    ) } : null,
                                    taskVizItems.length > 0 ? { key: 'viz', label: 'Visualization', children: (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                            {taskVizItems.map((v, vi) => renderVizOutput(v, vi, sqlFallback))}
                                        </div>
                                    ) } : null,
                                    taskNarrItems.length > 0 ? { key: 'narrative', label: 'Narrative', children: (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {taskNarrItems.map((n, ni) => <div key={ni}>{renderNarrativeContent(n)}</div>)}
                                        </div>
                                    ) } : null,
                                    taskEval ? { key: 'eval', label: 'Evaluation', children: renderTaskEvalMetrics(taskEval) } : null,
                                ].filter(Boolean) as Array<{ key: string; label: string; children: React.ReactNode }>;

                                return {
                                    key: String(i),
                                    label: (
                                        <Space>
                                            <Text strong>{titleCase(taskName)}</Text>
                                            {taskEval?.matched != null && (
                                                <Tag color={taskEval.matched ? 'success' : 'error'} style={{ margin: 0 }}>
                                                    {taskEval.matched ? 'Matched' : 'Unmatched'}
                                                </Tag>
                                            )}
                                        </Space>
                                    ),
                                    children: tabItems.length > 0
                                        ? <Tabs size="small" items={tabItems} />
                                        : <Text type="secondary">No per-task data</Text>,
                                };
                            })}
                        />
                    </Card>
                )}

                {/* Ground Truth */}
                {gt && Object.keys(gt).length > 0 && (
                    <Card size="small" title="Ground Truth">
                        <Descriptions size="small" column={1} bordered
                            labelStyle={{ width: 160, wordBreak: 'break-word' }}
                            contentStyle={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {knownGtFields.map(({ key, label }) => {
                                const val = gt[key];
                                if (val == null) return null;
                                return (
                                    <Descriptions.Item key={key} label={label}>
                                        {renderGtValue(key, val)}
                                    </Descriptions.Item>
                                );
                            })}
                            {unknownGtKeys.map((key) => (
                                <Descriptions.Item key={key} label={titleCase(key)}>
                                    {renderGtValue(key, gt[key])}
                                </Descriptions.Item>
                            ))}
                        </Descriptions>
                    </Card>
                )}
            </div>
        );
    };

    return (
        <Drawer
            title={run ? `Run #${run.id} — ${run.config_name}` : 'Eval Run Details'}
            width="100%"
            open={open}
            onClose={onClose}
            destroyOnClose
            extra={
                run?.status === 'completed' && (
                    <Space>
                        <Button
                            size="small"
                            icon={<DownloadOutlined />}
                            loading={downloading}
                            onClick={() => onDownload('csv')}
                        >
                            CSV
                        </Button>
                        <Button
                            size="small"
                            icon={<DownloadOutlined />}
                            loading={downloading}
                            onClick={() => onDownload('json')}
                        >
                            JSON
                        </Button>
                    </Space>
                )
            }
        >
            {loading && <Spin style={{ display: 'block', margin: '60px auto' }} />}
            {!loading && !run && <Empty description="No results available" />}
            {!loading && run && (
                <div style={{ maxWidth: 1400, margin: '0 auto' }}>
                    {/* ── A. Summary Stats Row ── */}
                    <Card size="small" style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                                <div style={{ marginTop: 4 }}>
                                    <Tag color={statusConfig[run.status].color} style={{ fontSize: 14, padding: '2px 12px' }}>
                                        {statusConfig[run.status].label}
                                    </Tag>
                                </div>
                            </div>
                            {totalCount > 0 && (
                                <Statistic
                                    title="Pass Rate"
                                    value={passCount}
                                    suffix={`/ ${totalCount}`}
                                    valueStyle={{ color: passCount === totalCount ? '#52c41a' : passCount === 0 ? '#ff4d4f' : '#faad14' }}
                                />
                            )}
                            <Statistic title="Questions" value={run.question_count ?? '-'} />
                            {run.repeats != null && (
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Repeats</Text>
                                    <div style={{ marginTop: 4 }}>
                                        <Tag color="blue" style={{ fontSize: 14, padding: '2px 12px' }}>{run.repeats}x</Tag>
                                    </div>
                                </div>
                            )}
                            <Statistic
                                title="Duration"
                                value={formatDuration(run.started_at, run.completed_at)}
                                prefix={<ClockCircleOutlined />}
                            />
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>Question Set</Text>
                                <div style={{ marginTop: 4 }}>
                                    <Text>{run.question_set ?? 'All'}</Text>
                                </div>
                            </div>
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>Target Agent</Text>
                                <div style={{ marginTop: 4 }}>
                                    <Text>{run.target_agent ?? 'Full pipeline'}</Text>
                                </div>
                            </div>
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>Triggered By</Text>
                                <div style={{ marginTop: 4 }}>
                                    <Text><UserOutlined style={{ marginRight: 4 }} />{run.triggered_by ?? '-'}</Text>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {run.config_snapshot && Object.keys(run.config_snapshot).length > 0 && (
                        <Collapse
                            size="small"
                            style={{ marginBottom: 16 }}
                            items={[{
                                key: 'config-snapshot',
                                label: (
                                    <Space>
                                        <SettingOutlined />
                                        <Text strong style={{ fontSize: 13 }}>Config Snapshot</Text>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            ({Object.keys(run.config_snapshot).length} keys)
                                        </Text>
                                    </Space>
                                ),
                                children: (
                                    <pre style={{
                                        fontSize: 11,
                                        margin: 0,
                                        maxHeight: 400,
                                        overflow: 'auto',
                                        background: '#fafafa',
                                        padding: 12,
                                        borderRadius: 4,
                                    }}>
                                        {JSON.stringify(run.config_snapshot, null, 2)}
                                    </pre>
                                ),
                            }]}
                        />
                    )}

                    {run.status === 'failed' && run.error_message && (
                        <Alert
                            type="error"
                            showIcon
                            message="Run Failed"
                            description={run.error_message}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {/* ── B. Metrics by Agent ── */}
                    {metricsGrouped && Object.keys(metricsGrouped).length > 0 && (
                        <>
                            <Title level={5} style={{ marginBottom: 12 }}>Metrics by Agent</Title>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                                {(results.agents_evaluated ?? Object.keys(metricsGrouped)).map((agent) => {
                                    const agentMetrics = metricsGrouped[agent];
                                    if (!agentMetrics) return null;
                                    return (
                                        <Card
                                            key={agent}
                                            size="small"
                                            title={titleCase(agent)}
                                            style={{ minWidth: 260, flex: '1 1 260px' }}
                                        >
                                            {Object.entries(agentMetrics)
                                                .filter(([k]) => isDisplayMetric(k))
                                                .map(([k, v]) => {
                                                    const pct = typeof v === 'number' ? metricPercent(v) : null;
                                                    return (
                                                        <div key={k} style={{ marginBottom: 8 }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                    {humanizeMetricKey(k)}
                                                                </Text>
                                                                <Text strong style={{ fontSize: 13 }}>
                                                                    {typeof v === 'number' ? v.toFixed(2) : String(v)}
                                                                </Text>
                                                            </div>
                                                            {pct != null && (
                                                                <Progress
                                                                    percent={pct}
                                                                    size="small"
                                                                    showInfo={false}
                                                                    strokeColor={scoreColor(pct)}
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                        </Card>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* ── Consistency Analysis ── */}
                    {consistency && (
                        <>
                            <Title level={5} style={{ marginBottom: 12 }}>Consistency Analysis</Title>
                            <Card size="small" style={{ marginBottom: 20 }}>
                                <Table
                                    size="small"
                                    pagination={false}
                                    rowKey="field"
                                    dataSource={Object.entries(consistency.aggregate).map(([field, agg]) => ({
                                        field,
                                        mean: agg.mean_exact_match,
                                        min: agg.min_exact_match,
                                    }))}
                                    columns={[
                                        { title: 'Field', dataIndex: 'field', key: 'field', render: (v: string) => titleCase(v) },
                                        {
                                            title: 'Mean Exact Match',
                                            dataIndex: 'mean',
                                            key: 'mean',
                                            width: 280,
                                            render: (v: number) => {
                                                const pct = Math.round(v * 100);
                                                return (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Progress
                                                            percent={pct}
                                                            size="small"
                                                            showInfo={false}
                                                            strokeColor={scoreColor(pct)}
                                                            style={{ flex: 1, margin: 0 }}
                                                        />
                                                        <Text strong style={{ fontSize: 12, minWidth: 36 }}>{pct}%</Text>
                                                    </div>
                                                );
                                            },
                                        },
                                        {
                                            title: 'Min Exact Match',
                                            dataIndex: 'min',
                                            key: 'min',
                                            width: 120,
                                            align: 'center',
                                            render: (v: number) => {
                                                const pct = Math.round(v * 100);
                                                return <Tag color={pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'error'}>{pct}%</Tag>;
                                            },
                                        },
                                    ]}
                                />

                                {consistency.per_question.length > 0 && (
                                    <div style={{ marginTop: 16 }}>
                                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Per-Question Drilldown</Text>
                                        <Collapse
                                            size="small"
                                            items={consistency.per_question.map((pq, idx) => ({
                                                key: String(idx),
                                                label: (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Text ellipsis style={{ flex: 1 }}>{pq.query}</Text>
                                                        <Tag>{pq.runs} runs</Tag>
                                                        {Object.entries(pq.fields).map(([f, d]) => (
                                                            <Tooltip key={f} title={`${titleCase(f)}: ${Math.round(d.exact_match_rate * 100)}%`}>
                                                                {d.exact_match_rate === 1.0
                                                                    ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                                    : d.exact_match_rate === 0.0
                                                                        ? <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                                                                        : <WarningOutlined style={{ color: '#faad14' }} />}
                                                            </Tooltip>
                                                        ))}
                                                    </div>
                                                ),
                                                children: (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                                        {Object.entries(pq.fields).map(([fieldName, detail]) => (
                                                            <div key={fieldName}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                                    <Text strong style={{ fontSize: 12 }}>{titleCase(fieldName)}</Text>
                                                                    <Tag color={detail.exact_match_rate === 1 ? 'success' : detail.exact_match_rate === 0 ? 'error' : 'warning'}>
                                                                        {Math.round(detail.exact_match_rate * 100)}% match
                                                                    </Tag>
                                                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                                                        {detail.unique_outputs} unique output{detail.unique_outputs !== 1 ? 's' : ''}
                                                                    </Text>
                                                                </div>
                                                                {Array.isArray(detail.outputs_per_run) && detail.outputs_per_run.length > 0 && (
                                                                    <Table
                                                                        size="small"
                                                                        pagination={false}
                                                                        rowKey="run_index"
                                                                        dataSource={detail.outputs_per_run}
                                                                        columns={[
                                                                            { title: 'Run', dataIndex: 'run_index', key: 'run_index', width: 60, align: 'center' },
                                                                            {
                                                                                title: 'Output',
                                                                                dataIndex: 'output',
                                                                                key: 'output',
                                                                                render: (v: unknown) => {
                                                                                    const text = v == null ? '-'
                                                                                        : typeof v === 'string' ? v
                                                                                        : JSON.stringify(v, null, 2);
                                                                                    return (
                                                                                        <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto' }}>
                                                                                            {text}
                                                                                        </div>
                                                                                    );
                                                                                },
                                                                            },
                                                                        ]}
                                                                    />
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ),
                                            }))}
                                        />
                                    </div>
                                )}
                            </Card>
                        </>
                    )}

                    {/* ── C + D. Per-Question Results ── */}
                    {questionRows.length > 0 && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                                    <Title level={5} style={{ margin: 0 }}>Per-Question Results</Title>
                                    <Text type="secondary">
                                        {passCount} of {totalCount} passed
                                        {errorCount > 0 && `, ${errorCount} error${errorCount > 1 ? 's' : ''}`}
                                    </Text>
                                </div>
                                <Segmented
                                    size="small"
                                    value={questionFilter}
                                    onChange={(v) => setQuestionFilter(v as QuestionFilter)}
                                    options={[
                                        { label: `All (${totalCount})`, value: 'all' },
                                        { label: `Passed (${passCount})`, value: 'passed' },
                                        { label: `Failed (${failCount})`, value: 'failed' },
                                        ...(errorCount > 0
                                            ? [{ label: `Errors (${errorCount})`, value: 'errors' as const }]
                                            : []),
                                    ]}
                                />
                            </div>
                            <Table
                                rowKey="question_index"
                                dataSource={filteredRows}
                                columns={questionColumns}
                                size="small"
                                pagination={false}
                                expandable={{ expandedRowRender: renderExpandedRow }}
                            />
                        </>
                    )}
                </div>
            )}
        </Drawer>
    );
};

export default SkillStudioEvalRunDetailDrawer;
